import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
  ArrowBackIosRounded,
  ArrowForwardIosRounded,
} from "@mui/icons-material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import MatchItem from "../../components/matchItem";
import { normalizeMatchData, buildDynamicApiUrl } from "../../utils/matchesDataMapper";

dayjs.extend(isSameOrAfter);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

const MatchesStandalone = ({ data, eventHandlers }) => {
  const [error, setError] = useState("");
  const [matches, setMatches] = useState([]);
  const [isInitiallyScrolled, setIsInitiallyScrolled] = useState(false);

  const scrollRef = useRef(null);
  const [scroll, setScroll] = useState({ showLeft: false, showRight: true });

  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  const [dragging, setDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [scrollStartX, setScrollStartX] = useState(0);

  const dataByDate = useMemo(() => {
    const data = matches.reduce((dateMap, item) => {
      if (!item.date) return dateMap;
      const localDate = dayjs.utc(item.date).local();
      const local = navigator.language;
      item.date = localDate.toISOString();
      item.time = !!item.time
        ? new Date(localDate).toLocaleTimeString(local, {
            hour: "numeric",
            minute: "numeric",
          })
        : "";
      const dateKey = dayjs(item.date).format("YYYY-MM-DD");
      dateMap.has(dateKey)
        ? dateMap.get(dateKey).push(item)
        : dateMap.set(dateKey, [item]);
      return dateMap;
    }, new Map());

    return data;
  }, [matches]);

  const matchStyles = useMemo(
    () => ({
      date: data.settings.date,
      header: data.settings.header,
      teams: data.settings.teams,
      showVenue: data.settings.showVenue,
      showLeague: data.settings.showLeague,
      showLogos: data.settings.showLogos,
    }),
    [data.settings]
  );

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        // Build dynamic URL if date range is provided
        let finalUrl = data.settings.url;
        
        const { daysBefore, daysAfter } = data.settings || {};
        const hasDateRange = !isNaN(daysBefore) || !isNaN(daysAfter);
        
        if (hasDateRange) {
          const dateRange = {
            daysBefore: daysBefore || 0,
            daysAfter: daysAfter || 0
          };
          finalUrl = buildDynamicApiUrl(data.settings.url, dateRange);
        }
        
        const res = await fetch(finalUrl);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        
        const rawData = await res.json();
        
        // Normalize data to handle both API formats
        const normalizedData = normalizeMatchData(rawData);
        
        setMatches(normalizedData.data || []);
      } catch (err) {
        console.error('Fetch matches error:', err);
        const errorMessage = `Failed to fetch data`;
        setError(errorMessage);
        if (eventHandlers?.onError) {
          eventHandlers.onError({
            type: 'fetch_error',
            message: errorMessage,
          });
        }
      }
    };

    fetchMatches();

    if (data.settings.fetchInterval) {
      const interval = setInterval(fetchMatches, data.settings.fetchInterval);
      return () => clearInterval(interval);
    }
  }, [data.settings.url, data.settings.fetchInterval, data.settings.daysBefore, data.settings.daysAfter]);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const showLeft = container.scrollLeft > 0;
    const showRight =
      container.scrollLeft + container.clientWidth + 1 < container.scrollWidth;
    setScroll({ showLeft, showRight });
  }, []);

  const scrollNext = useCallback(() => {
    const container = scrollRef.current;
    container.scroll({
      top: 0,
      left: container.scrollLeft + container.clientWidth,
      behavior: "smooth",
    });
  }, []);

  const scrollPrev = useCallback(() => {
    const container = scrollRef.current;
    container.scroll({
      top: 0,
      left: container.scrollLeft - container.clientWidth,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    container.style.userSelect = "none";
    container.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || matches.length === 0 || isInitiallyScrolled) return;

    const today = dayjs().startOf("day");
    const scrollToDate = matches.reduce((closestDate, item) => {
      const itemDate = dayjs(item.date).startOf("day");
      if (itemDate.isSameOrAfter(today)) {
        if (!closestDate || itemDate.isBefore(closestDate)) {
          closestDate = itemDate;
        }
      }
      return closestDate;
    }, null);

    const uniqueDays = Array.from(
      new Set(
        matches.map((item) =>
          dayjs(item.date).startOf("day").format("YYYY-MM-DD")
        )
      )
    );

    const index = scrollToDate
      ? uniqueDays.findIndex((day) => day === scrollToDate.format("YYYY-MM-DD"))
      : undefined;

    if (index >= 0) {
      const dateElement = document.getElementById(`date_${index}`);
      if (dateElement) {
        dateElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "start",
        });
      }
    } else {
      container.scroll({
        top: 0,
        left: container.scrollWidth,
        behavior: "smooth",
      });
    }

    setIsInitiallyScrolled(true);
    handleScroll();
  }, [matches, isInitiallyScrolled, handleScroll]);

  const handleDragStart = useCallback((e) => {
    setDragging(true);
    setDragStartX(e.clientX);
    setScrollStartX(scrollRef.current.scrollLeft);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDragging(false);
  }, []);

  useEffect(() => {
    const handleDragMove = (e) => {
      if (!dragging) return;
      const dragDistance = e.clientX - dragStartX;
      scrollRef.current.scrollLeft = scrollStartX - dragDistance;
    };

    if (dragging) {
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);

      return () => {
        document.removeEventListener("mousemove", handleDragMove);
        document.removeEventListener("mouseup", handleDragEnd);
      };
    }
  }, [dragging, dragStartX, scrollStartX, handleDragEnd]);

  const errorDisplay = (
    <div className="w-full h-full flex justify-center items-center">
      <div className="bg-black/90 p-5 rounded-md">
        <span className="text-white">{error}</span>
      </div>
    </div>
  );

  const renderMatches = useCallback(() => {
    const uniqueDates = Array.from(dataByDate.keys());

    return uniqueDates.map((dateStr, idx) => {
      const date = dayjs(dateStr);
      const dayFormatted = date.format("ddd");
      const dateFormatted = date.format("DD");
      const monthFormatted = date.format("MMM");
      const isToday = date.isSame(dayjs(), "day");
      const matchItems = dataByDate.get(dateStr).map((item, idx) => (
        <div
          key={`${item.gameId}_${idx}`}
          className="flex-grow-0 flex-shrink-0 h-full relative mr-0.5"
        >
          <MatchItem item={item} styles={matchStyles} />
        </div>
      ));

      return (
        <div key={`date-group-${dateStr}`} className="date-group flex">
          <div
            id={`date_${idx}`}
            className="flex flex-col justify-center items-center space-y-10 rounded sticky left-0 z-10 mr-0.5"
            style={{
              backgroundColor: data.settings.date.backgroundColor,
              color: data.settings.date.textColor,
              border: isToday
                ? `2px solid ${
                    data.settings.date.todayBorderColor ?? "#08152E"
                  }`
                : "",
              ...(data.settings.showLeague &&
                data.settings.showVenue && { height: "200px" }),
              width: "50px",
              position: "sticky",
              left: 0,
              boxShadow: "4px 0 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div className="flex flex-col items-center justify-center w-20 gap-1.5">
              <div className="flex flex-col items-center justify-center">
                <div className="font-bold text-lg leading-none">
                  {dateFormatted}
                </div>
                <div className="font-bold text-sm leading-none">
                  {monthFormatted}
                </div>
              </div>
              <hr
                style={{
                  width: "50%",
                  backgroundColor: data.settings.date.textColor,
                  opacity: "38%",
                  height: "1px",
                  border: "none",
                }}
              ></hr>
              <div className="text-sm leading-none">{dayFormatted}</div>
            </div>
          </div>
          <div className="matches-container flex">{matchItems}</div>
        </div>
      );
    });
  }, [dataByDate, matchStyles, data.settings.date]);

  const arrowStyles =
    "vf-scroll-button z-30 absolute bg-black text-white/70 w-12 h-12 bg-black/50 rounded-lg flex justify-center items-center hover:text-white hover:bg-black/90 cursor-pointer m-0.25";
  const backArrowStyles = `${arrowStyles} left-3 top-1/2 -translate-y-1/2`;
  const forwardArrowStyles = `${arrowStyles} right-3 top-1/2 -translate-y-1/2`;

  const matchContainer = (
    <div
      id={`widget-${data.id}`}
      className="overflow-y-hidden w-full relative my-2.5"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        ref={scrollRef}
        className="vf-team-vs-team overflow-y-hidden overflow-x-auto flex h-full w-full snap-x scrollbar-none gap-0.5"
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
      >
        {renderMatches()}
      </div>
      {isHovered && scroll.showLeft && (
        <div onClick={scrollPrev} className={backArrowStyles}>
          <ArrowBackIosRounded fontSize="large" />
        </div>
      )}
      {isHovered && scroll.showRight && (
        <div onClick={scrollNext} className={forwardArrowStyles}>
          <ArrowForwardIosRounded fontSize="large" />
        </div>
      )}
    </div>
  );

  return error ? errorDisplay : matchContainer;
};

export default memo(MatchesStandalone);
